import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  X,
  Camera
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FolhaPontoPWA = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [tipoPeriodo, setTipoPeriodo] = useState('mensal');
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [espelho, setEspelho] = useState(null);
  const [modalAssinatura, setModalAssinatura] = useState(false);
  const [senha, setSenha] = useState('');
  const [selfieCapturada, setSelfieCapturada] = useState(null);
  const [capturandoSelfie, setCapturandoSelfie] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const gerarEspelho = async () => {
    console.log('🔵 PWA - gerarEspelho chamado');
    console.log('👤 user:', user);
    console.log('📋 employee_id:', user?.employee_id);
    
    if (!user?.employee_id) {
      console.log('❌ Funcionário não identificado');
      toast({
        title: "Erro",
        description: "Funcionário não identificado",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    console.log('⏳ Loading iniciado');
    
    try {
      const token = localStorage.getItem('token');
      let periodo_inicio, periodo_fim;
      
      if (tipoPeriodo === 'mensal') {
        // Garantir que mes e ano sejam números válidos
        const mesNum = parseInt(mes, 10);
        const anoNum = parseInt(ano, 10);
        
        if (isNaN(mesNum) || isNaN(anoNum)) {
          console.log('❌ Mês ou ano inválido');
          toast({
            title: "Erro",
            description: "Mês ou ano inválido",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }
        
        // Calcular primeiro e último dia do mês
        periodo_inicio = `${anoNum}-${String(mesNum).padStart(2, '0')}-01`;
        const ultimoDia = new Date(anoNum, mesNum, 0).getDate();
        periodo_fim = `${anoNum}-${String(mesNum).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')}`;
        
        console.log(`📅 Período calculado: ${periodo_inicio} a ${periodo_fim}`);
      } else {
        if (!dataInicio || !dataFim) {
          console.log('❌ Datas não preenchidas');
          toast({
            title: "Erro",
            description: "Selecione as datas de início e fim",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }
        periodo_inicio = dataInicio;
        periodo_fim = dataFim;
        console.log(`📅 Período customizado: ${periodo_inicio} a ${periodo_fim}`);
      }

      console.log('🔑 Token:', token ? 'presente' : 'ausente');
      console.log('📡 Fazendo requisição POST...');

      const response = await axios.post(
        `${API}/api/timetracking/gerar-espelho`,
        {
          funcionario_id: user.employee_id,
          periodo_inicio: periodo_inicio,
          periodo_fim: periodo_fim,
          tipo_periodo: tipoPeriodo
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('✅ Resposta recebida:', response.data);
      setEspelho(response.data);
      toast({
        title: "Sucesso!",
        description: "Espelho de ponto gerado com sucesso",
        variant: "default"
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('❌ Erro ao gerar espelho:', error);
      console.error('❌ Detalhes:', error.response?.data);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || 'Erro ao gerar espelho de ponto',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      console.log('⏳ Loading finalizado');
    }
  };

  const abrirModalAssinatura = () => {
    if (!espelho) {
      toast({
        title: "Erro",
        description: "Gere o espelho antes de assinar",
        variant: "destructive"
      });
      return;
    }
    if (espelho.assinado) {
      toast({
        title: "Já Assinado",
        description: "Este espelho já foi assinado anteriormente",
        variant: "destructive"
      });
      return;
    }
    setModalAssinatura(true);
    setSenha('');
    setSelfieCapturada(null);
    iniciarCamera();
  };

  const iniciarCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      streamRef.current = stream;
      setCapturandoSelfie(true);
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      toast({
        title: "Erro",
        description: "Erro ao acessar câmera. Verifique as permissões.",
        variant: "destructive"
      });
    }
  };

  const capturarSelfie = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (video && canvas) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imagemBase64 = canvas.toDataURL('image/jpeg', 0.8);
      setSelfieCapturada(imagemBase64);
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      setCapturandoSelfie(false);
    }
  };

  const fecharModal = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setModalAssinatura(false);
    setSenha('');
    setSelfieCapturada(null);
    setCapturandoSelfie(false);
  };

  const assinarEspelho = async () => {
    if (!senha) {
      toast({
        title: "Erro",
        description: "Digite sua senha",
        variant: "destructive"
      });
      return;
    }
    if (!selfieCapturada) {
      toast({
        title: "Erro",
        description: "Capture a selfie antes de assinar",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/timetracking/assinar-espelho`,
        {
          espelho_id: espelho.id,
          senha: senha,
          selfie_base64: selfieCapturada
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: "✅ Assinado com Sucesso!",
        description: "Seu espelho de ponto foi assinado eletronicamente",
        variant: "default"
      });
      fecharModal();
      
      setEspelho({
        ...espelho,
        assinado: true,
        data_assinatura: response.data.assinado_em || new Date().toISOString(),
        pdf_url: response.data.pdf_url
      });
    } catch (error) {
      console.error('Erro ao assinar:', error);
      toast({
        title: "Erro na Assinatura",
        description: error.response?.data?.detail || 'Erro ao assinar espelho',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => window.history.back()}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex items-center space-x-2">
            <FileText className="h-6 w-6" />
            <h1 className="text-xl font-bold">Folha de Ponto</h1>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Card de Seleção */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Selecionar Período</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Tipo de Período */}
            <div className="flex space-x-4">
              <button
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  tipoPeriodo === 'mensal'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
                onClick={() => setTipoPeriodo('mensal')}
              >
                Mensal
              </button>
              <button
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  tipoPeriodo === 'customizado'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
                onClick={() => setTipoPeriodo('customizado')}
              >
                Customizado
              </button>
            </div>

            {/* Seleção Mensal */}
            {tipoPeriodo === 'mensal' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">Mês</Label>
                  <select
                    value={mes}
                    onChange={(e) => setMes(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(2000, i).toLocaleString('pt-BR', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Ano</Label>
                  <Input
                    type="number"
                    value={ano}
                    onChange={(e) => setAno(parseInt(e.target.value))}
                    min={2020}
                    max={2030}
                  />
                </div>
              </div>
            )}

            {/* Seleção Customizada */}
            {tipoPeriodo === 'customizado' && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm">Data Início</Label>
                  <Input
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Data Fim</Label>
                  <Input
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                  />
                </div>
              </div>
            )}

            <Button
              onClick={gerarEspelho}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 h-12"
            >
              {loading ? 'Gerando...' : 'Gerar Folha de Ponto'}
            </Button>
          </CardContent>
        </Card>

        {/* Visualização do Espelho */}
        {espelho && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">FOLHA DE PONTO</CardTitle>
                  <p className="text-sm text-gray-600 mt-1">
                    {new Date(espelho.periodo_inicio).toLocaleDateString('pt-BR')} a{' '}
                    {new Date(espelho.periodo_fim).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                {espelho.assinado && (
                  <Badge className="bg-green-600">✓ Assinado</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Dados Resumidos */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                <div><strong>Nome:</strong> {espelho.funcionario_nome}</div>
                <div><strong>CPF:</strong> {espelho.funcionario_cpf}</div>
                <div><strong>Cargo:</strong> {espelho.funcionario_cargo}</div>
                <div><strong>Empresa:</strong> {espelho.empresa_nome}</div>
              </div>

              {/* Totalizadores */}
              <div className="bg-blue-50 rounded-lg p-3">
                <h3 className="font-semibold mb-2 text-sm">Totalizadores</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="text-gray-600">Trabalhadas</div>
                    <div className="font-bold text-lg">{espelho.totalizadores.total_horas_trabalhadas}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Extras</div>
                    <div className="font-bold text-lg">{espelho.totalizadores.total_horas_extras_diurnas}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Atrasos</div>
                    <div className="font-bold text-lg">{espelho.totalizadores.total_atrasos}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Faltas</div>
                    <div className="font-bold text-lg">{espelho.totalizadores.dias_faltosos}</div>
                  </div>
                </div>
              </div>

              {/* Registros Diários (Scrollável) */}
              <div>
                <h3 className="font-semibold mb-2 text-sm">Registros Diários</h3>
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="p-2 text-left">Data</th>
                          <th className="p-2 text-left">Pontos</th>
                          <th className="p-2 text-center">Horas</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {espelho.registros_diarios.map((reg, idx) => (
                          <tr key={idx} className={reg.dia_semana.includes('sábado') || reg.dia_semana.includes('Domingo') ? 'bg-gray-50' : ''}>
                            <td className="p-2">
                              <div className="font-medium">{new Date(reg.data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</div>
                              <div className="text-gray-600">{reg.dia_semana}</div>
                            </td>
                            <td className="p-2 text-xs">
                              {reg.marcacoes.length > 0 ? reg.marcacoes.join(' ') : '-'}
                            </td>
                            <td className="p-2 text-center font-medium">{reg.horas_trabalhadas}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Botão de Assinatura */}
              {!espelho.assinado ? (
                <Button
                  onClick={abrirModalAssinatura}
                  className="w-full bg-green-600 hover:bg-green-700 h-12"
                >
                  ✍️ Assinar Eletronicamente
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div className="text-sm">
                      <div className="font-semibold text-green-800">Assinado Eletronicamente</div>
                      <div className="text-green-700">
                        {new Date(espelho.data_assinatura).toLocaleString('pt-BR')}
                      </div>
                    </div>
                  </div>
                  
                  {/* Botão Download PDF */}
                  <Button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const response = await axios.get(
                          `${API}/api/timetracking/download-pdf-espelho/${espelho.id}`,
                          {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: 'blob'
                          }
                        );
                        
                        // Criar URL do blob e fazer download
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `folha_ponto_${espelho.funcionario_nome}_${espelho.periodo_inicio}.pdf`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        
                        toast({
                          title: "Sucesso!",
                          description: "PDF baixado com sucesso",
                          variant: "default"
                        });
                      } catch (error) {
                        console.error('Erro ao baixar PDF:', error);
                        toast({
                          title: "Erro",
                          description: "Erro ao baixar PDF",
                          variant: "destructive"
                        });
                      }
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 h-12 flex items-center justify-center space-x-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    <span>Baixar PDF Assinado</span>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal de Assinatura */}
      {modalAssinatura && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">Assinar Folha</h2>
                <button onClick={fecharModal} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-2">
                <Label>Senha</Label>
                <Input
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="Digite sua senha"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label>Selfie de Confirmação</Label>
                {!selfieCapturada ? (
                  <>
                    <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <Button
                      onClick={capturarSelfie}
                      disabled={!capturandoSelfie}
                      className="w-full h-12"
                    >
                      <Camera className="h-5 w-5 mr-2" />
                      Capturar Selfie
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                      <img
                        src={selfieCapturada}
                        alt="Selfie"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <Button
                      onClick={() => {
                        setSelfieCapturada(null);
                        iniciarCamera();
                      }}
                      variant="outline"
                      className="w-full h-12"
                    >
                      Tirar Nova Foto
                    </Button>
                  </>
                )}
              </div>

              <canvas ref={canvasRef} style={{ display: 'none' }} />

              <Button
                onClick={assinarEspelho}
                disabled={loading || !senha || !selfieCapturada}
                className="w-full bg-green-600 hover:bg-green-700 h-12"
              >
                {loading ? 'Assinando...' : '✍️ Confirmar Assinatura'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FolhaPontoPWA;
